import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2, GripVertical, Send, Bold, Italic, List, ListOrdered } from "lucide-react";
import { api, img, errMsg } from "@/lib/api";
import { Field } from "@/components/Layout";

const RichEditor = ({ value, onChange }) => {
  const ref = useRef(null);
  const [len, setLen] = useState(() => (value || "").replace(/<[^>]+>/g, "").length);
  const cmd = (c) => {
    document.execCommand(c);
    ref.current.focus();
    onChange(ref.current.innerHTML);
  };
  return (
    <div className="border border-[#E3D9F0] rounded-xl bg-[#FFFEFF] focus-within:border-[#7E5BAF]">
      <div className="flex gap-1 p-2 border-b border-[#E3D9F0]">
        {[["bold", Bold], ["italic", Italic], ["insertUnorderedList", List], ["insertOrderedList", ListOrdered]].map(([c, I]) => (
          <button type="button" key={c} onClick={() => cmd(c)} className="p-2 rounded hover:bg-[#EEE7F6]" data-testid={`rte-${c}`}><I size={16} /></button>
        ))}
        <span className={`ml-auto text-xs self-center ${len > 5000 ? "text-red-700" : "text-[#786E65]"}`} data-testid="rte-count">{len}/5000</span>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning className="rich min-h-[180px] p-4 outline-none" data-testid="rte-content"
        dangerouslySetInnerHTML={{ __html: value }}
        onInput={(e) => { setLen(e.currentTarget.innerText.length); onChange(e.currentTarget.innerHTML); }} />
    </div>
  );
};

const Slots = ({ immagini, reload }) => {
  const [drag, setDrag] = useState(null);
  const [busy, setBusy] = useState(null);
  const bySlot = Object.fromEntries(immagini.map((i) => [i.slot, i]));

  const upload = async (slot, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("L'immagine supera i 5MB");
    setBusy(slot);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/operatore/vetrina/immagini/${slot}`, fd);
      toast.success(`Immagine caricata nello slot ${slot}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const drop = async (target) => {
    if (drag == null || drag === target) return;
    const order = [1, 2, 3, 4, 5].map((s) => bySlot[s]).filter(Boolean);
    const from = order.findIndex((i) => i.slot === drag);
    let to = order.findIndex((i) => i.slot === target);
    if (to < 0) to = order.length - 1;
    const [m] = order.splice(from, 1);
    order.splice(to, 0, m);
    try {
      await api.put("/operatore/vetrina/immagini-ordine", { ids: order.map((i) => i.id) });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDrag(null);
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="image-slots">
      {[1, 2, 3, 4, 5].map((s) => {
        const im = bySlot[s];
        return (
          <div key={s} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(s)} data-testid={`slot-${s}`}
            className={`relative aspect-[3/2] rounded-xl border-2 border-dashed overflow-hidden ${s === 1 ? "col-span-2 md:row-span-2 md:aspect-auto" : ""} ${im ? "border-transparent" : "border-[#E3D9F0] bg-[#FAF7FD]"}`}>
            {im ? (
              <div draggable onDragStart={() => setDrag(s)} className="w-full h-full cursor-grab group">
                <img src={img(im.path_400)} alt="" className="w-full h-full object-cover" />
                <span className="absolute top-2 left-2 bg-[#FFFEFF]/90 rounded-lg px-2 py-1 text-xs font-semibold flex items-center gap-1"><GripVertical size={12} />{s === 1 ? "Copertina" : `Slot ${s}`}</span>
                <div className="absolute bottom-2 right-2 flex gap-1 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                  <label className="bg-[#FFFEFF] rounded-lg p-2 cursor-pointer" title="Sostituisci"><ImagePlus size={14} />
                    <input type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => upload(s, e.target.files[0])} />
                  </label>
                  <button className="bg-[#FFFEFF] rounded-lg p-2" data-testid={`slot-${s}-delete`} onClick={async () => { await api.delete(`/operatore/vetrina/immagini/${s}`); reload(); }}><Trash2 size={14} /></button>
                </div>
              </div>
            ) : (
              <label className="w-full h-full min-h-[120px] grid place-items-center cursor-pointer text-center text-xs text-[#786E65] p-3 hover:bg-[#EEE7F6] transition-colors">
                <span><ImagePlus className="mx-auto mb-1 text-[#7E5BAF]" />{busy === s ? "Caricamento…" : s === 1 ? "Copertina (slot 1)" : `Slot ${s}`}<br />JPG/PNG/WebP · min 1200×800</span>
                <input type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => upload(s, e.target.files[0])} data-testid={`slot-${s}-input`} />
              </label>
            )}
          </div>
        );
      })}
    </div>
  );
};

const toEuroList = (arr) => (arr || []).map((c) => c / 100).join(", ");

export default function VetrinaEditor({ st, reload }) {
  const b = st.vetrina.bozza || {};
  const [f, setF] = useState({
    titolo: b.titolo || "", descrizione_html: b.descrizione_html || "", tagli: toEuroList(b.tagli_cent), importo_libero: !!b.importo_libero,
    importo_min: b.importo_min_cent ? b.importo_min_cent / 100 : "", importo_max: b.importo_max_cent ? b.importo_max_cent / 100 : "",
    validita_mesi: b.validita_mesi || 12, frazionabile: b.frazionabile ?? true, tipo_buono: b.tipo_buono || "multiuso",
    condizioni: b.condizioni || "", dove_riscattare: b.dove_riscattare || "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const salva = async () => {
    const body = {
      titolo: f.titolo, descrizione_html: f.descrizione_html, validita_mesi: Number(f.validita_mesi), frazionabile: f.frazionabile,
      tipo_buono: f.tipo_buono, condizioni: f.condizioni, dove_riscattare: f.dove_riscattare, importo_libero: f.importo_libero,
      tagli_cent: f.tagli.split(/[,;\s]+/).filter(Boolean).map((x) => Math.round(parseFloat(x.replace(",", ".")) * 100)).filter((x) => x > 0),
      importo_min_cent: f.importo_libero ? Math.round(Number(f.importo_min) * 100) : null,
      importo_max_cent: f.importo_libero ? Math.round(Number(f.importo_max) * 100) : null,
    };
    await api.put("/operatore/vetrina/prodotto", body);
  };

  const run = async (fn, ok) => {
    try {
      await fn();
      toast.success(ok);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const v = st.vetrina;
  return (
    <div className="space-y-10">
      {v.stato_revisione === "rifiutata" && v.note_rifiuto && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="rifiuto-note">
          <strong>Vetrina non approvata.</strong> Note: {v.note_rifiuto}
        </div>
      )}
      {v.pubblicata && v.stato_revisione !== "approvata" && (
        <p className="text-sm text-[#786E65]" data-testid="versione-pubblicata-info">La versione approvata in precedenza resta visibile al pubblico finché le modifiche non vengono approvate.</p>
      )}
      <section>
        <h3 className="font-display text-2xl">Immagini della vetrina</h3>
        <p className="text-sm text-[#786E65] mb-4">Esattamente 5 immagini. Trascina per riordinare: lo slot 1 è la copertina. Max 5MB, minimo 1200×800px.</p>
        <Slots immagini={st.immagini} reload={reload} />
      </section>
      <section className="card grid md:grid-cols-2 gap-5">
        <h3 className="font-display text-2xl md:col-span-2">Pagina prodotto</h3>
        <div className="md:col-span-2"><Field label={`Titolo (${f.titolo.length}/80)`}><input maxLength={80} className="field" value={f.titolo} onChange={set("titolo")} data-testid="prod-titolo-input" /></Field></div>
        <div className="md:col-span-2"><span className="label">Descrizione</span><RichEditor value={f.descrizione_html} onChange={(h) => setF((p) => ({ ...p, descrizione_html: h }))} /></div>
        <Field label="Tagli fissi in € (separati da virgola)" hint="Es. 25, 50, 100"><input className="field" value={f.tagli} onChange={set("tagli")} data-testid="prod-tagli-input" /></Field>
        <div>
          <label className="flex items-center gap-2 mt-7"><input type="checkbox" checked={f.importo_libero} onChange={set("importo_libero")} data-testid="prod-libero-checkbox" />Consenti importo libero</label>
          {f.importo_libero && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <input className="field" placeholder="Min €" value={f.importo_min} onChange={set("importo_min")} data-testid="prod-min-input" />
              <input className="field" placeholder="Max €" value={f.importo_max} onChange={set("importo_max")} data-testid="prod-max-input" />
            </div>
          )}
        </div>
        <Field label="Validità (mesi)"><input type="number" min={1} max={60} className="field" value={f.validita_mesi} onChange={set("validita_mesi")} data-testid="prod-validita-input" /></Field>
        <Field label="Tipo di buono">
          <select className="field" value={f.tipo_buono} onChange={set("tipo_buono")} data-testid="prod-tipo-select">
            <option value="multiuso">Multiuso (più utilizzi fino a esaurimento)</option>
            <option value="monouso">Monouso (un solo utilizzo, l'eventuale residuo si annulla)</option>
          </select>
        </Field>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.frazionabile} onChange={set("frazionabile")} data-testid="prod-frazionabile-checkbox" />Frazionabile (riscatto parziale)</label>
        <div />
        <Field label="Condizioni d'uso"><textarea rows={4} maxLength={3000} className="field" value={f.condizioni} onChange={set("condizioni")} data-testid="prod-condizioni-input" /></Field>
        <Field label="Dove riscattare"><textarea rows={4} maxLength={500} className="field" value={f.dove_riscattare} onChange={set("dove_riscattare")} data-testid="prod-dove-input" /></Field>
        <div className="md:col-span-2 flex flex-wrap gap-3">
          <button className="btn-secondary" onClick={() => run(salva, "Bozza salvata")} data-testid="prod-salva-btn">Salva bozza</button>
          <button className="btn-primary" disabled={v.stato_revisione === "in_revisione"} data-testid="vetrina-invia-btn"
            onClick={() => run(async () => { await salva(); await api.post("/operatore/vetrina/invia"); }, "Vetrina inviata in revisione")}>
            <Send size={16} />{v.stato_revisione === "in_revisione" ? "In revisione" : "Invia in revisione"}
          </button>
        </div>
      </section>
      <LogoUpload logo={st.operatore.logo_path} reload={reload} />
    </div>
  );
}

const LogoUpload = ({ logo, reload }) => (
  <section className="card flex items-center gap-6">
    <div className="w-20 h-20 rounded-xl border border-[#E3D9F0] bg-[#FAF7FD] overflow-hidden grid place-items-center">
      {logo ? <img src={img(logo)} alt="Logo" className="w-full h-full object-contain" data-testid="logo-preview" /> : <span className="text-xs text-[#786E65]">Logo</span>}
    </div>
    <div>
      <h3 className="font-display text-xl">Logo dell'attività</h3>
      <p className="text-sm text-[#786E65]">Compare nel PDF della gift card. Minimo 200×200px.</p>
      <label className="btn-secondary btn-sm mt-2 cursor-pointer">Carica logo
        <input type="file" hidden accept="image/jpeg,image/png,image/webp" data-testid="logo-input" onChange={async (e) => {
          const fd = new FormData();
          fd.append("file", e.target.files[0]);
          try { await api.post("/operatore/logo", fd); toast.success("Logo aggiornato"); reload(); } catch (er) { toast.error(errMsg(er)); }
        }} />
      </label>
    </div>
  </section>
);
